import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import BaseCardLayoutActions from "../base-card-layout/base-card-layout-actions";
import OrganizationService from '../../utilities/services/organization-service';
import { Organization } from '../../models/interfaces/organization';
import { OrganizationStatus } from '../../models/enumerations/organization-status';
import { AppContent } from '../../models/interfaces/app-content';
import { AppContentType } from '../../models/enumerations/app-content-type';
import { useAuthState } from '../../utilities/contexts/auth-state-context';
import { useParams } from 'react-router-dom';
import { TextTypeInput } from '../forms';
import { useTranslation } from 'react-i18next';
import LeadLinksService from '../../utilities/services/lead-links-service';
import { IndustryListOptions, IndustryValue } from '../../models/industry';
import { Utils } from '../../utilities/utils';

interface CreateOrganizationProps {
  onBackClick: () => void;
  onSubmitClick: () => void;
  onOrganizationCreate: (data: any, product: any) => void;
}
interface LinkPaymentParams {
  id: string;
  userId: string;
  type?: string;
}
const CreateOrganization: React.FC<CreateOrganizationProps> = ({ onSubmitClick, onBackClick, onOrganizationCreate }) => {
  const COMPONENT_CLASS = "c-subscriptions";
  const FORM_COMPONENT_CLASS = "p-link-create-account";
  const [organizationError, setOrganizationError] = useState("");
  const { id } = useParams<LinkPaymentParams>();
  const { t } = useTranslation();
  const { state } = useAuthState();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onBlur', reValidateMode: 'onSubmit', defaultValues: {
      organizationName: state.user?.npeTemp && state.user?.npeTemp?.orgName ? state.user?.npeTemp.orgName :
        state.temp.organization ? state.temp.organization.name : "",
      industry:
        (state.user?.npeTemp && state.user?.npeTemp?.orgIndustry) ? Utils.getIndustryValue(state.user?.npeTemp?.orgIndustry) :
          state.user?.industry ? state.user?.industry.value :
            state.temp.organization ? state.temp.organization.industry.value :
              IndustryValue.Athletic,
      otherIndustry: state.temp.organization && state.temp.organization.industry ? state.temp.organization.industry : ""
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const onSubmit = async (data: any) => {
    const { organizationName, industry, otherIndustry } = data;

    let leadLink = id ? await LeadLinksService.get(id) : null;

    if (organizationName && industry) {
      if (await OrganizationService.nameExists(organizationName)) {
        setOrganizationError(t('pricing.organizationPage.err_alreadyExists'));
        return;
      }
      setOrganizationError('');
      let org: Organization;
      org = {
        appContent: {
          [AppContentType.PAIN]: {} as AppContent,
          [AppContentType.BEHAVIORAL_HEALTH]: {} as AppContent,
        },
        status: OrganizationStatus.Active,
        name: organizationName,
        leadLinkId: id ? id : '',
        orgDefaultContentConfig: leadLink ? leadLink.orgDefaultContentConfig : undefined,
        industry: state.user?.industry ? Utils.getIndustryObject(state.user?.industry.value) :
          industry === IndustryValue.Other ? otherIndustry : Utils.getIndustryObject(industry)
      };
      onOrganizationCreate(org, state.temp.product);
    } else {
      setOrganizationError(t('forms.orgName_val'));
    }
  }
  const selectedOption = watch("industry");

  return (
    <>
      <div className={`${COMPONENT_CLASS}__header`}>
        {t('pricing.organizationPage.title')}
      </div>
      <div style={{ marginLeft: 'auto', marginRight: 'auto', marginBottom: '1rem', textAlign: "center" }}>
        {t('pricing.organizationPage.subTitle')}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`${FORM_COMPONENT_CLASS}__sign-up-content`} style={{ height: '345px' }}>
          <div className={`${FORM_COMPONENT_CLASS}__field`}>
            {errors.organizationName && (
              <div role="alert" className={`${FORM_COMPONENT_CLASS} -error`}>
                {t('forms.orgName_val')}
              </div>
            )}
            {organizationError && (
              <div role="alert" className={`${FORM_COMPONENT_CLASS} -error`}>
                {organizationError}
              </div>
            )}
            <TextTypeInput
              readonly={state.user?.npeTemp ? true : false}
              id='organizationName'
              label={t('forms.orgName')}
              hideLabel={true}
              inputClassName={`c-input__input ${errors.organizationName && `${FORM_COMPONENT_CLASS} -showerror`}`}
              type='text'
              registerHook={register}
              registerOptions={{
                required: true
              }}
              errorState={errors.organizationName}
            />
          </div>
          {!state.user?.industry &&
            <>
              <div className={`${FORM_COMPONENT_CLASS}__field`}>
                {errors.industry && (
                  <div role="alert" className={`${FORM_COMPONENT_CLASS} -error`}>
                    {t('pricing.organizationPage.selectIndustry')}
                  </div>
                )}
                <select id="industry"
                  className={`c-input__input ${errors.industry && `${FORM_COMPONENT_CLASS} -showerror`}`}
                  {...register("industry", {
                    required: true,
                    value: state.temp.organization ? Utils.getIndustryValue(state.temp.organization.industry) : IndustryValue.Athletic
                  })}
                  style={{ display: 'flex' }}
                >
                  {IndustryListOptions.map((option) =>
                    <option value={option.value} disabled={state.user?.npeTemp ? true : false}>{option.label}</option>
                  )}
                </select>
              </div>
              {selectedOption === IndustryValue.Other && (
                <div className={`${FORM_COMPONENT_CLASS}__field`}>
                  {errors.otherIndustry && (
                    <div role="alert" className={`${FORM_COMPONENT_CLASS} -error`}>
                      {t('pricing.organizationPage.enterIndustry')}
                    </div>
                  )}
                  <TextTypeInput
                    readonly={state.user?.npeTemp ? true : false}
                    id='otherIndustry'
                    label={t('pricing.organizationPage.industry')}
                    hideLabel={true}
                    inputClassName={`c-input__input ${errors.otherIndustry && `${FORM_COMPONENT_CLASS} -showerror`}`}
                    registerHook={register}
                    registerOptions={{
                      required: true,
                    }}
                    errorState={errors.otherIndustry} />
                </div>
              )}
            </>
          }
        </div>
      </form>
      <BaseCardLayoutActions submitButton={true} backButton={true} onBack={onBackClick} onSubmit={handleSubmit(onSubmit)} submitButtonText={state.temp.product === 'STARTER' ? t('pricing.organizationPage.btn_createAccount') : t('buttons.btn_continue')}></BaseCardLayoutActions>
    </>
  );
}

export default CreateOrganization;